import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { auth } from "@/api/firebase";
import { logout } from "@/hooks/common/auth";

type AccountAvatarProps = {
  size?: number;
  className?: string;
  title?: string;
  onClick?: () => void;
};

export function AccountAvatar({ size = 32, className = "", title, onClick }: AccountAvatarProps) {
  const user = auth.currentUser;
  const rawPhotoUrl = user?.photoURL || "";
  const [imageError, setImageError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [pressed, setPressed] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Normalize Google photo URL to a reasonable size to improve cache hits and avoid unnecessary bandwidth
  const photoUrl = useMemo(() => {
    if (!rawPhotoUrl) return "";
    if (/([?&]sz=\d+)|\bs\d+-c\b/.test(rawPhotoUrl)) return rawPhotoUrl;
    const sep = rawPhotoUrl.includes("?") ? "&" : "?";
    const target = Math.max(24, Math.min(256, Math.round(size)));
    return `${rawPhotoUrl}${sep}sz=${target}`;
  }, [rawPhotoUrl, size]);

  // Reset load state when URL changes
  useEffect(() => {
    setLoaded(false);
    setImageError(false);
  }, [photoUrl]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const display =
    user?.displayName?.trim() || user?.email?.trim() || user?.uid?.slice(0, 6) || "?";
  const initials = display
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  const fontSize = Math.max(12, Math.floor(size * 0.45));

  const showImage = !!photoUrl && loaded && !imageError;

  return (
    <div ref={rootRef} className={["relative inline-block", className].join(" ")}>
      <button
        type="button"
        aria-label="User account"
        title={title || display}
        onClick={() => {
          onClick?.();
          setOpen((v) => !v);
        }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onMouseLeave={() => setPressed(false)}
        className={[
          "inline-flex items-center justify-center rounded-full overflow-hidden transition",
          open || pressed ? "border-2" : "border",
          "border-gray-300 shadow-md",
          showImage ? "bg-white" : "bg-gradient-to-br from-blue-500 to-purple-600",
          "hover:border-2 hover:shadow-lg",
        ].join(" ")}
        style={{ width: size, height: size }}
      >
      {showImage ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={photoUrl}
          alt="User avatar"
          loading="lazy"
          onError={() => setImageError(true)}
          onLoad={() => setLoaded(true)}
          style={{ width: size, height: size, objectFit: "cover" }}
        />
      ) : (
        <span
          className="font-bold text-white uppercase"
          style={{ fontSize }}
        >
          {initials || "?"}
        </span>
      )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 mt-2 min-w-[160px] rounded-md border border-gray-200 bg-white text-gray-900 shadow-lg z-50"
        >
          <div className="px-3 pt-2 pb-1 text-sm text-gray-500">
            {user?.displayName || display}
          </div>
          <div className="px-3 pb-2 text-sm text-gray-500">
            {user?.email || "-"}
          </div>
          <div className="h-px bg-gray-200" />
          <button
            role="menuitem"
            className="block w-full text-left px-3 py-2 text-sm hover:bg-black hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            Account
          </button>
          <button
            role="menuitem"
            className="block w-full text-left px-3 py-2 text-sm hover:bg-black hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              void logout();
            }}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}


